import React, { useState } from "react";
import { DataButton } from "../../Objects/interfaces/AppInterface";
import SelectAuto from "../SelectAuto";
import ButtonField from "../Ui/ButtonField";
import { Paper } from "@mui/material";

export interface NavigationLayoutProps {
  dataBtn1: DataButton[];
  dataBtn2?: DataButton[];
  isHere?: boolean;
  activeId?: string;
  onActiveIdChange?: (id: string) => void;
  value?: number;
  setValue?: React.Dispatch<React.SetStateAction<number>>;
  classes?: { fontFamily?: string; primaryText?: string; primaryBorder?: string; text?: string; navHeader?: string };
  customNav1?: string;
}

export interface Nav2LayoutProps {
  dataBtn2?: DataButton[];
  activeId?: string;
  onActiveIdChange?: (id: string) => void;
  classes?: { fontFamily?: string; primaryText?: string; primaryBorder?: string; text?: string };
}

const getValueFromId = (buttons: DataButton[] | undefined, id: string | undefined): number =>
  id != null ? (buttons?.find((b) => b.id === id)?.value ?? buttons?.[0]?.value ?? 0) : buttons?.[0]?.value ?? 0;

const getIdFromValue = (buttons: DataButton[] | undefined, value: number): string | undefined =>
  buttons?.find((b) => b.value === value)?.id;

const isActiveById = (btn: DataButton, activeId: string | undefined): boolean =>
  activeId != null && btn.id != null && btn.id === activeId;

export const Nav2Layout: React.FC<Nav2LayoutProps> = ({
  dataBtn2,
  activeId: controlledActiveId,
  onActiveIdChange,
  classes: propsClasses,
}) => {
  const [localActiveId, setLocalActiveId] = useState<string | undefined>(
    () => dataBtn2?.find((b) => b.id != null)?.id ?? dataBtn2?.[0]?.id
  );
  const activeId = controlledActiveId ?? localActiveId;

  if (!dataBtn2?.length) return null;

  const handleClick = (btn: DataButton) => {
    if (btn.id != null) {
      onActiveIdChange?.(btn.id);
      if (controlledActiveId === undefined) setLocalActiveId(btn.id);
    }
    btn.handleChange?.();
  };

  return (
    <nav
      className="hidden lg:flex flex-row items-center justify-end gap-2"
      aria-label="Navigation secondaire"
    >
      {/* <Paper sx={{ p: 2, mb: 2 }}> */}
      {dataBtn2.map((btn, index) => (
        <ButtonField
          key={btn.id ?? `nav2-${index}`}
          label={btn.title as string}
          variant={isActiveById(btn, activeId) ? "contained" : "outlined"}
          className={`font-semibold min-w-0 ${btn.classCustom ?? ""}`}
          onClick={() => handleClick(btn)}
        />
      ))}
      {/* </Paper> */}
    </nav>
  );
};


const NavigationLayout: React.FC<NavigationLayoutProps> = (props) => {
  const [localActiveId, setLocalActiveId] = useState<string | undefined>(() => {
    const withId = (arr: DataButton[] | undefined) => arr?.find((b) => b.id != null)?.id ?? arr?.[0]?.id;
    return withId(props.dataBtn2) ?? withId(props.dataBtn1);
  });

  const activeId = props.activeId ?? localActiveId;
  const setActiveId = (id: string) => {
    props.onActiveIdChange?.(id);
    if (props.activeId === undefined) setLocalActiveId(id);
    const btn = [...(props.dataBtn2 ?? []), ...(props.dataBtn1 ?? [])].find((b) => b.id === id);
    if (btn != null && props.setValue) props.setValue(btn.value);
  };

  const valueForSelect = getValueFromId(props.dataBtn2, activeId);
  const handleSelectChange = (v: number) => {
    const id = getIdFromValue(props.dataBtn2, v);
    if (id != null) setActiveId(id);
    if (props.setValue) props.setValue(v);
    const btn = props.dataBtn2?.find((b) => b.value === v);
    btn?.handleChange?.();
  };

  const handleNav1Click = (btn: DataButton) => {
    if (btn.id != null) setActiveId(btn.id);
    if (btn.id == null && props.setValue) props.setValue(btn.value);
    btn.handleChange?.();
  };

  const filteredBtn1 = props.dataBtn1?.filter(btn => !btn.classCustom?.includes("hidden"));
  const filteredBtn2 = props.dataBtn2?.filter(btn => !btn.classCustom?.includes("hidden"));

  const rawValue1 = props.value !== undefined ? props.value : valueForSelect;
  const valueForBtn1 = filteredBtn1?.some(b => b.value === rawValue1)
    ? rawValue1
    : (filteredBtn1?.[0]?.value ?? 0);

  const rawValue2 = props.value !== undefined ? props.value : valueForSelect;
  const valueForBtn2 = filteredBtn2?.some(b => b.value === rawValue2)
    ? rawValue2
    : (filteredBtn2?.[0]?.value ?? 0);

  return (
    <div className="flex flex-col gap-4 py-3 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
      <div className="lg:hidden w-full min-w-0">
        <SelectAuto
          data={filteredBtn1}
          value={valueForBtn1}
          setValue={props.setValue ?? handleSelectChange}
          customClass="bg-white border border-gray-200 shadow-sm p-2 w-full font-medium"
        />
      </div>
      <nav
        className={`lg:flex hidden flex-wrap items-center gap-2 ${props.customNav1 ?? ""}`}
        aria-label="Navigation principale"
      >
        {props.dataBtn1?.map((btn, index) => (
          <ButtonField
            key={btn.id ?? index}
            label={btn.title as string}
            variant={isActiveById(btn, activeId) ? "contained" : "outlined"}
            className={`font-semibold lg:min-w-0 ${btn.classCustom ?? props.classes?.navHeader ?? ""}`}
            onClick={() => handleNav1Click(btn)}
          />
        ))}
      </nav>

      {!props.isHere && props.dataBtn2 != null && props.dataBtn2.length > 0 && (
        <>
          <div className="lg:hidden w-full min-w-0">
            <SelectAuto
              data={filteredBtn2}
              value={valueForBtn2}
              setValue={props.setValue ?? handleSelectChange}
              customClass="bg-white border border-gray-200 shadow-sm p-2 w-full font-medium"
            />
          </div>
          <Nav2Layout
            dataBtn2={props.dataBtn2}
            activeId={activeId}
            onActiveIdChange={setActiveId}
            classes={props.classes}
          />
        </>
      )}
    </div>
  );
};

export default NavigationLayout;
